import { useEffect, useMemo, useState } from "react";
import { Instagram } from "lucide-react";
import InstagramFeed from "@/components/InstagramFeed";
import { trackOutbound } from "@/lib/analytics";

const IG_URL = "https://www.instagram.com/mujeres_motor";
const BEHOLD_TIMEOUT_MS = 6000;

// ---------------------------------------------------------------------------
// Elegant fallback CTA — used when there are 0 posts or fetch fails.
// ---------------------------------------------------------------------------
function ElegantFallback({ headline, body }) {
  return (
    <div
      data-testid="instagram-fallback"
      className="rounded-md border border-charcoal/10 bg-cream-dark/30 px-6 py-12 md:py-16 text-center max-w-3xl mx-auto"
    >
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-charcoal text-cream mb-5">
        <Instagram className="h-6 w-6" strokeWidth={1.5} />
      </div>
      <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-2">@mujeres_motor</p>
      <h3 className="font-serif text-2xl md:text-3xl text-charcoal font-light max-w-md mx-auto leading-tight">
        {headline || (
          <>
            Lo más fresco está pasando en{" "}
            <span className="italic">nuestro Instagram.</span>
          </>
        )}
      </h3>
      <p className="text-sm text-charcoal/65 mt-3 max-w-md mx-auto">
        {body || "Detrás de cámaras, drops editoriales y avances de cada historia que estamos filmando ahora."}
      </p>
      <a
        href={IG_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="instagram-fallback-cta"
        onClick={() => trackOutbound(IG_URL, { surface: "instagram_fallback" })}
        className="inline-flex items-center gap-2 mt-7 border border-terracotta text-terracotta hover:bg-terracotta hover:text-cream transition-colors rounded-full px-6 py-2.5 text-sm uppercase tracking-[0.18em]"
      >
        <Instagram className="h-4 w-4" /> Síguenos en Instagram
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton — three subtle shimmering tiles in cream/charcoal so we
// never flash gray boxes. Only renders during the fetch window.
// ---------------------------------------------------------------------------
function FeedSkeleton() {
  return (
    <div
      data-testid="instagram-skeleton"
      className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto"
      aria-hidden="true"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="aspect-square rounded-sm bg-cream-dark/50 animate-pulse"
          style={{ animationDelay: `${i * 120}ms`, animationDuration: "1.6s" }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pull a usable image URL out of the Behold post's `sizes` map. Behold
// returns several sizes; we prefer medium, then large, then small, then
// the raw mediaUrl as a last resort.
// ---------------------------------------------------------------------------
function pickImage(post) {
  const sizes = post?.sizes || {};
  return (
    sizes.medium?.mediaUrl ||
    sizes.large?.mediaUrl ||
    sizes.small?.mediaUrl ||
    post?.mediaUrl ||
    null
  );
}

// ---------------------------------------------------------------------------
// Single tile — used by every layout variant. Always renders a real post;
// never a placeholder.
// ---------------------------------------------------------------------------
function PostTile({ post, surface, sizeClass, captionLines = 2 }) {
  const img = pickImage(post);
  const caption = (post.prunedCaption || post.caption || "").trim();
  return (
    <a
      href={post.permalink}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`ig-post-${post.id}`}
      onClick={() => trackOutbound(post.permalink, { surface })}
      className={`group block rounded-sm overflow-hidden bg-charcoal/5 ${sizeClass}`}
    >
      <div className="relative aspect-square overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={caption.slice(0, 90) || "Mujeres Motor en Instagram"}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        ) : (
          // No image (extremely rare — only when Behold mediaUrl expired);
          // do NOT render an empty grey box. Show a stylised IG card instead.
          <div className="w-full h-full bg-gradient-to-br from-charcoal to-[#2a2a2a] flex items-center justify-center text-cream">
            <Instagram className="h-9 w-9 opacity-80" strokeWidth={1.4} />
          </div>
        )}
        <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/30 transition-colors duration-500" />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-cream/0 group-hover:text-cream transition-colors duration-500">
          <Instagram className="h-3 w-3" strokeWidth={1.6} /> @mujeres_motor
        </div>
      </div>
      {caption && (
        <div className="px-3 py-3 bg-cream-dark/40 border-t border-charcoal/10">
          <p
            className="text-xs md:text-sm text-charcoal/80 font-serif italic leading-snug"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: captionLines,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            “{caption}”
          </p>
        </div>
      )}
    </a>
  );
}

// ---------------------------------------------------------------------------
// Smart layout dispatcher — picks the right grid based on post count so we
// never paint empty placeholder slots.
// ---------------------------------------------------------------------------
function PostsLayout({ posts }) {
  const n = posts.length;
  if (n === 1) {
    // Editorial: single post centred, with explanatory copy + CTA on the
    // sides on desktop. On mobile it stacks gracefully.
    const post = posts[0];
    return (
      <div
        data-testid="instagram-grid-1"
        className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center max-w-5xl mx-auto"
      >
        <div className="md:col-span-7 mx-auto w-full max-w-[480px]">
          <PostTile post={post} surface="instagram_section_solo" captionLines={4} />
        </div>
        <div className="md:col-span-5 text-left">
          <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-3">
            @mujeres_motor
          </p>
          <h3 className="font-serif text-2xl md:text-[2rem] leading-tight text-charcoal font-light">
            Lo más fresco está{" "}
            <span className="italic">en nuestro feed.</span>
          </h3>
          <p className="text-sm text-charcoal/70 mt-4 leading-relaxed">
            Detrás de cámaras, drops editoriales y avances de cada historia que estamos filmando ahora.
            Más posts vienen pronto.
          </p>
          <a
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-cta"
            onClick={() => trackOutbound(IG_URL, { surface: "instagram_section" })}
            className="inline-flex items-center gap-2 mt-6 border border-terracotta text-terracotta hover:bg-terracotta hover:text-cream transition-colors rounded-full px-5 py-2 text-xs uppercase tracking-[0.22em]"
          >
            <Instagram className="h-3.5 w-3.5" /> Síguenos en Instagram
          </a>
        </div>
      </div>
    );
  }

  if (n === 2) {
    return (
      <div
        data-testid="instagram-grid-2"
        className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 max-w-3xl mx-auto"
      >
        {posts.map((p) => (
          <PostTile key={p.id} post={p} surface="instagram_section_pair" captionLines={3} />
        ))}
      </div>
    );
  }

  // 3, 4, 5 → fill exactly that many cells in a 3-col grid (no empty slots).
  if (n >= 3 && n <= 5) {
    return (
      <div
        data-testid={`instagram-grid-${n}`}
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto"
      >
        {posts.map((p) => (
          <PostTile key={p.id} post={p} surface="instagram_section" captionLines={2} />
        ))}
      </div>
    );
  }

  // 6+ → cap at 6, render the canonical 3×2 grid.
  return (
    <div
      data-testid="instagram-grid-6"
      className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto"
    >
      {posts.slice(0, 6).map((p) => (
        <PostTile key={p.id} post={p} surface="instagram_section" captionLines={2} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Behold JSON consumer · v2.
//
// Why we left the <behold-widget> web component behind: its shadow DOM
// rendered fixed 6 slots regardless of post count, leaving 5 empty grey
// boxes when an account had only 1 published post. Behold's public JSON
// endpoint (https://feeds.behold.so/<feedId>) gives us the raw post list
// so we can choose a layout proportional to the real count.
// ---------------------------------------------------------------------------
function BeholdHost({ widgetId }) {
  // states: 'loading' | 'ready' | 'empty' | 'error'
  const [status, setStatus] = useState("loading");
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), BEHOLD_TIMEOUT_MS);

    fetch(`https://feeds.behold.so/${widgetId}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.posts) ? data.posts : [];
        if (list.length === 0) {
          setStatus("empty");
        } else {
          setPosts(list);
          setStatus("ready");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[Instagram] Behold feed fetch failed:", err?.message || err);
        setStatus("error");
      })
      .finally(() => clearTimeout(t));

    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(t);
    };
  }, [widgetId]);

  if (status === "loading") {
    return <FeedSkeleton />;
  }
  if (status === "empty" || status === "error") {
    return <ElegantFallback />;
  }
  return (
    <div data-testid="instagram-feed-grid" className="behold-wrap">
      <PostsLayout posts={posts} />
      {posts.length >= 2 && (
        <div className="text-center mt-10">
          <a
            href={IG_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-cta"
            onClick={() => trackOutbound(IG_URL, { surface: "instagram_section" })}
            className="inline-flex items-center gap-2 border border-terracotta text-terracotta hover:bg-terracotta hover:text-cream transition-colors rounded-full px-5 py-2 text-xs uppercase tracking-[0.22em]"
          >
            <Instagram className="h-3.5 w-3.5" /> Ver más en Instagram
          </a>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
export default function InstagramBlock() {
  const widgetId = useMemo(() => (process.env.REACT_APP_BEHOLD_WIDGET_ID || "").trim(), []);

  if (widgetId) {
    return <BeholdHost widgetId={widgetId} />;
  }
  // No Behold ID → admin-curated feed; falls back to elegant CTA when empty.
  return <FeedOrFallback />;
}

function FeedOrFallback() {
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    // We probe the InstagramFeed's outcome by listening for the
    // empty-feed sentinel. InstagramFeed itself returns null when empty;
    // we mirror that here with a short timer to avoid a render flash.
    const t = setTimeout(() => {
      const node = document.querySelector('[data-testid="instagram-feed"]');
      setStatus(node ? "ready" : "empty");
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  if (status === "empty") return <ElegantFallback />;
  return <InstagramFeed limit={8} />;
}
